import { useState } from 'react';
import type { FormFieldOption } from '@hcc/types';
import { Button } from '../../ui/Button';
import { Plus, X, ClipboardList, ArrowUp, ArrowDown } from 'lucide-react';

interface OptionsEditorProps {
  options: FormFieldOption[];
  onChange: (next: FormFieldOption[]) => void;
}

function slugValue(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'option';
}

/**
 * An option's value is its stable id: conditional fields and stored answers
 * reference it. We derive it from the label only while it is still the
 * auto-generated placeholder; once it holds a real value, renaming the label
 * leaves it untouched so conditions don't break when wording is tweaked.
 */
function isAutoValue(value: string): boolean {
  return value === '' || /^option_\d+$/.test(value);
}

/** Parse pasted lines into options. One per line; optional `value|label`. */
function parseBulk(text: string): FormFieldOption[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [a, b] = l.split('|').map((s) => s.trim());
      return b ? { value: slugValue(a!), label: b } : { value: slugValue(a!), label: a! };
    });
}

/** Edits a choice list (dropdown / radio / checkbox, or a grid select column). */
export function OptionsEditor({ options, onChange }: OptionsEditorProps) {
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');

  function update(i: number, patch: Partial<FormFieldOption>) {
    onChange(options.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= options.length) return;
    const next = options.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  }
  function add() {
    onChange([...options, { value: slugValue(`option ${options.length + 1}`), label: `Option ${options.length + 1}` }]);
  }

  // Append pasted options, skipping any whose value already exists (here or
  // earlier in the paste) so values stay unique without surprising the user.
  const parsed = parseBulk(bulkText);
  const existing = new Set(options.map((o) => o.value));
  const additions: FormFieldOption[] = [];
  const seen = new Set(existing);
  for (const o of parsed) {
    if (seen.has(o.value)) continue;
    seen.add(o.value);
    additions.push(o);
  }
  function importBulk() {
    if (additions.length) onChange([...options, ...additions]);
    setBulkText('');
    setBulkOpen(false);
  }

  return (
    <div className="space-y-2">
      {options.map((o, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={o.label}
            placeholder="Label"
            onChange={(e) => update(i, { label: e.target.value })}
            onBlur={() => {
              // Set the stable value from the label only on first entry (while
              // the value is still the placeholder). Later label edits never
              // touch it, so conditions / answers keyed on the value survive.
              if (isAutoValue(o.value) && o.label.trim()) {
                update(i, { value: slugValue(o.label) });
              }
            }}
            className="flex-1 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm"
          />
          <input
            value={o.value}
            placeholder="value"
            onChange={(e) => update(i, { value: e.target.value })}
            className="w-32 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm text-fg-muted"
          />
          <button
            type="button"
            disabled={i === 0}
            onClick={() => move(i, -1)}
            className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface-2 disabled:opacity-30"
            aria-label="Move option up"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={i === options.length - 1}
            onClick={() => move(i, 1)}
            className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface-2 disabled:opacity-30"
            aria-label="Move option down"
          >
            <ArrowDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onChange(options.filter((_, j) => j !== i))}
            className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface-2 hover:text-danger"
            aria-label="Remove option"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}

      {options.length > 0 ? (
        <p className="text-xs text-fg-dim">
          The right-hand value is a stable id used by conditional fields and reports. It is set
          from the label when you add an option and then stays put, so renaming a label later
          will not break anything. Change a value only if you know no condition depends on it.
        </p>
      ) : null}

      {bulkOpen ? (
        <div className="rounded-token-sm border border-line/10 bg-surface-2 p-2">
          <label className="block text-xs text-fg-muted">
            Paste options, one per line. Optional <code>value|label</code> per line.
            <textarea
              rows={5}
              autoFocus
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={'Small\nMedium\nLarge\n\nor: s|Small'}
              className="mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </label>
          <div className="mt-2 flex items-center gap-2">
            <Button variant="primary" size="sm" onClick={importBulk} disabled={additions.length === 0}>
              {additions.length > 0 ? `Add ${additions.length} option${additions.length === 1 ? '' : 's'}` : 'Add options'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setBulkText(''); setBulkOpen(false); }}>
              Cancel
            </Button>
            {bulkText.trim() && additions.length < parsed.length ? (
              <span className="text-xs text-fg-dim">{parsed.length - additions.length} duplicate(s) skipped</span>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<Plus className="h-4 w-4" />} onClick={add}>
            Add option
          </Button>
          <Button variant="ghost" size="sm" iconLeft={<ClipboardList className="h-4 w-4" />} onClick={() => setBulkOpen(true)}>
            Paste list
          </Button>
        </div>
      )}
    </div>
  );
}
